import * as THREE from "three";
import type { Sight, CameraModeConfig } from "../../types";
import type { CameraConfig } from "../../cameraConfig";
import { defaultCameraConfig } from "../../cameraConfig";
import type { CameraDirector, OrbitAnimationConfig } from "./CameraDirector";

/**
 * Manages camera transitions to points of interest (sights) using per-sight camera params
 */
export class SightManager {
  private camera: THREE.PerspectiveCamera;
  private director: CameraDirector;
  private cameraConfig: CameraConfig;
  private activeSight: Sight | null = null;
  private orbitAnimationEnabled: boolean = false;

  constructor(
    camera: THREE.PerspectiveCamera,
    director: CameraDirector,
    cameraConfig?: CameraConfig
  ) {
    this.camera = camera;
    this.director = director;
    this.cameraConfig = cameraConfig ? { ...cameraConfig } : { ...defaultCameraConfig };
  }

  private getCameraSetupForSight(sight: Sight): {
    position: THREE.Vector3;
    target: THREE.Vector3;
    fov?: number;
  } {
    if (!sight.cameraConfig) {
      throw new Error("Sight is missing cameraConfig.");
    }
    const { distance, height = 0, azimuth, polar, fov } = sight.cameraConfig;
    if (distance === undefined || azimuth === undefined || polar === undefined) {
      throw new Error("Sight cameraConfig is missing required params.");
    }

    const x = distance * Math.sin(polar) * Math.cos(azimuth);
    const y = distance * Math.cos(polar) + height;
    const z = distance * Math.sin(polar) * Math.sin(azimuth);

    return {
      position: new THREE.Vector3(
        sight.position.x + x,
        sight.position.y + y,
        sight.position.z + z
      ),
      target: new THREE.Vector3(
        sight.position.x,
        sight.position.y,
        sight.position.z
      ),
      fov,
    };
  }

  /**
   * Immediately moves the camera to the specified sight using the sight's camera config
   */
  moveToSight(sight: Sight): void {
    this.activeSight = sight;
    const sightPosition = this.getSightPosition(sight);
    const cameraSetup = this.getCameraSetupForSight(sight);
    this.director.moveToPose({
      position: cameraSetup.position,
      target: cameraSetup.target,
      fov: cameraSetup.fov,
    }, false);

    if (this.shouldAnimateSight()) {
      this.startOrbitAnimation(sightPosition, sight.cameraConfig);
    } else {
      this.stopOrbitAnimation();
    }
  }

  /**
   * Smoothly transitions the camera to the specified sight using the sight's camera config
   */
  transitionToSight(
    sight: Sight,
    duration?: number,
    onComplete?: () => void,
    currentTime?: number
  ): void {
    this.activeSight = sight;

    const transitionDuration = duration ?? sight.transitionDuration ?? 1.0;
    const sightPosition = this.getSightPosition(sight);
    const cameraSetup = this.getCameraSetupForSight(sight);

    this.director.transitionToPose(
      {
        position: cameraSetup.position,
        target: cameraSetup.target,
        fov: cameraSetup.fov,
      },
      transitionDuration,
      () => {
        if (this.activeSight && this.shouldAnimateSight()) {
          this.startOrbitAnimationForSight(this.activeSight);
        } else {
          this.stopOrbitAnimation();
        }
        onComplete?.();
      },
      currentTime,
      false
    );
  }

  /**
   * Updates the camera transition. Call this in your animation loop.
   */
  update(currentTime: number): void {
    this.director.update(currentTime);
  }

  /**
   * Check if a transition is currently in progress
   */
  isInTransition(): boolean {
    return this.director.isInTransition();
  }

  /**
   * Cancel any ongoing transition
   */
  cancelTransition(): void {
    this.director.cancelTransition();
  }

  startOrbitAnimationForSight(sight: Sight): void {
    const sightPosition = this.getSightPosition(sight);
    this.startOrbitAnimation(sightPosition, sight.cameraConfig);
  }

  setOrbitAnimationEnabled(enabled: boolean): void {
    this.orbitAnimationEnabled = enabled;
    this.director.setOrbitAnimationEnabled(enabled);
  }

  stopOrbitAnimation(): void {
    this.director.stopOrbitAnimation();
  }

  /**
   * Updates the camera configuration used by the manager and reapplies constraints.
   */
  setCameraConfig(config: CameraConfig): void {
    this.cameraConfig = { ...config };
    this.director.setCameraConfig(config);
  }

  private shouldAnimateSight(): boolean {
    return this.orbitAnimationEnabled;
  }

  private startOrbitAnimation(
    sightPosition: THREE.Vector3,
    config?: CameraModeConfig
  ): void {
    const cameraOffset = this.camera.position.clone().sub(sightPosition);
    const spherical = new THREE.Spherical().setFromVector3(cameraOffset);
    const baseAzimuth = Math.atan2(cameraOffset.z, cameraOffset.x);
    const basePolar = spherical.phi;
    const distance = config?.distance ?? cameraOffset.length();
    const height = config?.height ?? 0;
    const azimuthOffsetMin = config?.orbitAzimuthMin ?? -Math.PI / 4;
    const azimuthOffsetMax = config?.orbitAzimuthMax ?? Math.PI / 4;
    const polarOffsetMin = config?.orbitPolarMin ?? -Math.PI / 6;
    const polarOffsetMax = config?.orbitPolarMax ?? Math.PI / 6;
    const azimuthMin = baseAzimuth + azimuthOffsetMin;
    const azimuthMax = baseAzimuth + azimuthOffsetMax;
    const polarMin = basePolar + polarOffsetMin;
    const polarMax = basePolar + polarOffsetMax;
    const polarMinClamped = THREE.MathUtils.clamp(
      Math.min(polarMin, polarMax),
      0,
      Math.PI
    );
    const polarMaxClamped = THREE.MathUtils.clamp(
      Math.max(polarMin, polarMax),
      0,
      Math.PI
    );

    const orbitConfig: OrbitAnimationConfig = {
      sightPosition: sightPosition.clone(),
      distance,
      height,
      azimuthMin: Math.min(azimuthMin, azimuthMax),
      azimuthMax: Math.max(azimuthMin, azimuthMax),
      polarMin: polarMinClamped,
      polarMax: polarMaxClamped,
    };

    this.director.startOrbitAnimation(orbitConfig);
  }

  private getSightPosition(sight: Sight): THREE.Vector3 {
    return new THREE.Vector3(sight.position.x, sight.position.y, sight.position.z);
  }
}
